import React, { useState } from 'react';
import {
    Alert,
    Paper,
    Button,
    CircularProgress,
    Container,
    IconButton,
    InputAdornment,
    TextField,
    Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import FormError from "../components/Forms/FormError";
import { Helmet } from 'react-helmet-async';

import { setNewPassword } from '../api/auth';

const loginSchema = (t) =>
    yup.object().shape({
        password: yup
            .string()
            .min(6, t('errors.password_min'))
            .required(t('errors.password_required')),
        passwordConfirm: yup
            .string()
            .oneOf([yup.ref("password"), null], t('errors.password_not_match'))
            .required(t('errors.confirm_password_required')),
    });

const Login = () => {
    const [params] = useSearchParams();
    const resetToken = params.get('token');
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(loginSchema(t)),
    });

    const handleClickShowPassword = () => setShowPassword((prev) => !prev);
    const handleMouseDownPassword = (event) => event.preventDefault();

    const handleClickShowPasswordConfirm = () => setShowPasswordConfirm((prev) => !prev);
    const handleMouseDownPasswordConfirm = (event) => event.preventDefault();

    const onSubmit = async (data) => {
        if (data.password !== data.passwordConfirm) {
            setErrorMessage(t('errors.password_not_match'));
            return;
        }

        setLoading(true);

        try {
            const result = await setNewPassword(data.password, resetToken);
            if (result) {
                // TODO replace with proper alert component
                alert(t("reset_password.reset_successful"));
                navigate('/ienakt');
            } else {
                // TODO replace with proper alert component
                setErrorMessage(t("errors.err"));
                // navigate('/ienakt');
            }
        } catch (error) {
            setErrorMessage(t("errors.err"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Helmet>
                <title>{t("seo.title.reset_password")}</title>
                <meta property="og:title" content={t('seo.title.reset_password')} />

                <meta name="robots" content="noindex" />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '80vh',
                    bgcolor: 'background.paper',
                }}
                color="primary"
            >
                <Paper

                    sx={{
                        width: '100%',
                        maxWidth: '400px',
                        p: 3,
                        border: '1px solid lightgray',
                        bgcolor: '#fcfcfc',
                    }}
                >
                    <Typography color="primary" variant="h2" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'left' }}>
                        {t('reset_password.reset_page_title')}
                    </Typography>

                    {errorMessage && (
                        <FormError errorMessage={errorMessage} />
                    )}

                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            label={t('forms.new_password')}
                            type={showPassword ? 'text' : 'password'}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            {...register('password')}
                            error={!!errors.password}
                            helperText={errors.password?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ mr: '5px' }}>
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPassword}
                                            onMouseDown={handleMouseDownPassword}
                                            edge="end"
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label={t('forms.confirm_password')}
                            type={showPasswordConfirm ? 'text' : 'password'}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                            {...register('passwordConfirm')}
                            error={!!errors.passwordConfirm}
                            helperText={errors.passwordConfirm?.message}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" sx={{ mr: '5px' }}>
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={handleClickShowPasswordConfirm}
                                            onMouseDown={handleMouseDownPasswordConfirm}
                                            edge="end"
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            disableElevation
                            fullWidth
                            color='secondary'
                            size="large"
                            sx={{
                                mt: 2, mb: 1,
                                // STRIPE SHADOW
                                // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
                                '&:hover': {
                                    backgroundColor: 'secondary.light',
                                    // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                                }
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : t('reset_password.reset_page_submit')}
                        </Button>
                    </form>
                </Paper>
            </Container>
        </>
    );
};

export default Login;
